import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import SEOHead from '../components/SEOHead';
import StructuredData from '../components/StructuredData';
import { ArrowRight, Sparkles, Smartphone, CheckCircle2 } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';
import FAQ from '../components/FAQ';
import ExitIntentPopup from '../components/ExitIntentPopup';
import ServicesOverview from '../components/ServicesOverview';
import Testimonials from '../components/Testimonials';
import TrustStrip from '../components/TrustStrip';

const Home = () => {
  const { t, i18n } = useTranslation();
  const { locale, localizedPath, isRTL } = useLocale();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead page="home" />
      <StructuredData type="organization" />
      <StructuredData type="localbusiness" />
      <StructuredData type="website" />
      <div className="min-h-screen">
      {/* AI assistance banner - Hidden on mobile, visible on desktop */}
      <div className="hidden md:block bg-gradient-to-r from-[#0B3F84] to-blue-600 text-white py-3 px-4 mt-[60px]">
        <div className="container max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-4 text-center">
            <span className="text-sm font-medium">
              {t('home.bannerText')}
            </span>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(localizedPath('assessment'))}
                className="px-4 py-2 bg-white text-[#0B3F84] text-sm font-semibold rounded-lg hover:bg-gray-100 whitespace-nowrap"
              >
                {t('home.bannerButton')}
              </button>
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="px-4 py-2 bg-transparent border-2 border-white text-white text-sm font-semibold rounded-lg hover:bg-white hover:text-[#0B3F84] whitespace-nowrap"
              >
                {t('home.bannerContact')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip: social proof between banner and hero */}
      <TrustStrip />

      {/* Hero Section */}
      <section className="pt-20 pb-20 px-4">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-6 leading-tight">
              {t('home.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="px-8 py-4 bg-[#0B3F84] text-white text-lg font-semibold rounded-lg hover:bg-[#0a3570] pulse-cta inline-flex items-center gap-2"
                data-testid="hero-cta-consultation"
              >
                {t('home.ctaConsultation', 'Book a Free Consultation')}
                <ArrowRight size={20} />
              </button>
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="px-8 py-4 bg-white text-[#0B3F84] text-lg font-semibold rounded-lg border-2 border-[#0B3F84] hover:bg-[#0B3F84] hover:text-white"
                data-testid="hero-cta-quote"
              >
                {t('home.ctaQuote', 'Request a Quote')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-4">
              {t('home.servicesTitle')}
            </h2>
            <p className="text-xl text-gray-600">{t('home.servicesSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Service 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-gray-200 card-hover">
              <div className="w-14 h-14 bg-[#0B3F84] rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">
                {t('home.aiWebsite')}
              </h3>
              <p className="text-gray-600">
                {t('home.aiWebsiteDesc')}
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-gray-200 card-hover">
              <div className="w-14 h-14 bg-[#0B3F84] rounded-xl flex items-center justify-center mb-6">
                <Smartphone className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">
                {t('home.mobileApp')}
              </h3>
              <p className="text-gray-600">
                {t('home.mobileAppDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview - 7 SEO service pages */}
      <ServicesOverview />

      {/* Why Choose Us */}
      <section className="section bg-gradient-to-br from-gray-50 to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-10 md:p-12 rounded-2xl shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-6">
                {t('home.whyTitle')}
              </h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0B3F84] flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg text-gray-700">{t('home.whyPoint1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0B3F84] flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg text-gray-700">{t('home.whyPoint2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0B3F84] flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg text-gray-700">{t('home.whyPoint3')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0B3F84] flex-shrink-0 mt-1" size={24} />
                  <span className="text-lg text-gray-700">{t('home.whyPoint4')}</span>
                </li>
              </ul>
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="px-6 py-3 bg-[#0B3F84] text-white font-semibold rounded-lg hover:bg-[#0a3570] inline-flex items-center gap-2"
              >
                {t('home.startProject')}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Testimonials */}
      <Testimonials />

      {/* Blog Teaser */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('home.blogTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { titleKey: 'home.blogPost1', tag: 'AI' },
              { titleKey: 'home.blogPost2', tag: 'Performance' },
              { titleKey: 'home.blogPost3', tag: 'Product' }
            ].map((post, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 card-hover cursor-pointer"
                onClick={() => navigate(localizedPath('blog'))}
              >
                <div className="inline-block px-3 py-1 bg-[#0B3F84] text-white text-xs font-semibold rounded-full mb-4">
                  {post.tag}
                </div>
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">
                  {t(post.titleKey)}
                </h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate(localizedPath('blog'))}
              className="px-6 py-3 text-[#0B3F84] font-semibold hover:underline inline-flex items-center gap-2"
            >
              {t('home.viewAllArticles')}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section bg-gradient-to-br from-gray-50 to-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A0A0A] mb-4">
              {t('home.faqTitle')}
            </h2>
          </div>
          <FAQ key={i18n.language} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0A0A0A] mb-6">
              {t('home.contactTitle')}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {t('home.contactSubtitle')}
            </p>
            <button
              onClick={() => setIsContactFormOpen(true)}
              className="px-8 py-4 bg-[#0B3F84] text-white text-lg font-semibold rounded-lg hover:bg-[#0a3570] pulse-cta inline-flex items-center gap-2"
            >
              {t('home.getInTouch')}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Modals */}
      <ContactFormModal
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
      <ExitIntentPopup />
      </div>
    </>
  );
};

export default Home;
